import { useDispatch, useSelector } from "react-redux";
import * as fabric from "fabric";
import { Button } from "@/components/ui/button";
import {
  ImagePlus,
  Palette,
  Slash,
  Trash,
  Type,
  Download,
  Save,
  ShoppingCart,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CANVAS_CONFIG,
  DEFAULT_TEXT_CONFIG,
  TSHIRT_TYPES,
  TSHIRT_COLOR_CODES,
} from "../constants/designConstants";
import { setSelectedType, setTshirtColor } from "../features/tshirtSlice";
import { useRef, useState } from "react";
import { useCanvas } from "@/hooks/useCanvas";
import canvasStorageManager from "@/utils/canvasStorageManager";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "../../context/CartContext";

const ToolBar = ({ manualSync }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const selectedType = useSelector((state) => state.tshirt.selectedType);
  const tshirtColor = useSelector((state) => state.tshirt.tshirtColor);
  const { activeCanvas, selectedObject, frontCanvas, backCanvas } = useCanvas();
  const { toast } = useToast();
  const { addDesignToCart } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleTypeChange = (value) => {
    console.log("Selected Tshirt " + value);
    dispatch(setSelectedType(value));
  };

  const handleColorChange = (color) => {
    dispatch(setTshirtColor(color));
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddImage = (e) => {
    if (!activeCanvas || !e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      const imgObj = new Image();
      imgObj.src = event.target.result;

      imgObj.onload = () => {
        const image = new fabric.Image(imgObj);

        const maxWidth = CANVAS_CONFIG.width * 0.5;
        const maxHeight = CANVAS_CONFIG.height * 0.5;

        if (image.width > maxWidth || image.height > maxHeight) {
          const scale = Math.min(
            maxWidth / image.width,
            maxHeight / image.height
          );
          image.scale(scale);
        }

        image.set({
          left: (activeCanvas.width - image.getScaledWidth()) / 2,
          top: (activeCanvas.height - image.getScaledHeight()) / 2,
        });

        activeCanvas.add(image);
        activeCanvas.setActiveObject(image);
        activeCanvas.renderAll();
      };
    };

    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleAddText = () => {
    if (!activeCanvas) return;

    const text = new fabric.Textbox("Add Your Text Here...", {
      ...DEFAULT_TEXT_CONFIG,
      left: activeCanvas.width / 2,
      top: activeCanvas.height / 2,
      width: 200,
      editable: false,
    });

    activeCanvas.add(text);
    activeCanvas.setActiveObject(text);
    activeCanvas.renderAll();
  };

  const handleAddLine = () => {
    if (!activeCanvas) return;

    const line = new fabric.Line([100, 200, 250, 200], {
      stroke: "black",
      strokeWidth: 3,
      selectable: true,
      hasControls: true,
      strokeLineCap: "round",
    });

    activeCanvas.add(line);
    activeCanvas.setActiveObject(line);
    activeCanvas.renderAll();
  };

  const handleDelete = () => {
    if (!activeCanvas || !selectedObject) return;

    activeCanvas.remove(selectedObject);
    activeCanvas.discardActiveObject();
    activeCanvas.renderAll();
    manualSync();
  };

  const handleClearAll = () => {
    if (!activeCanvas) return;

    activeCanvas.clear();
    canvasStorageManager.clearCanvasStorage("all");
    activeCanvas.renderAll();
    manualSync();
  };

  // Generate canvas image as base64
  const getCanvasImage = async (canvasRef, trimWhitespace = false) => {
    if (!canvasRef) return null;

    const canvas = canvasRef.toCanvas ? await canvasRef.toCanvas() : canvasRef;

    // Ensure it’s not blank
    const ctx = canvas.getContext("2d");
    const pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const isBlank = !Array.from(pixelData).some((val) => val !== 0);
    if (isBlank) return null;

    let dataURL = canvas.toDataURL("image/png");
    if (trimWhitespace) dataURL = dataURL.trim();
    return dataURL;
  };

  // Add to Cart Handler
  const handleAddToCart = async () => {
    try {
      setIsAddingToCart(true);

      if (!frontCanvas && !backCanvas) {
        toast({
          variant: "destructive",
          title: "No Design Found",
          description: "Please create a design before adding to cart.",
          duration: 3000,
        });
        return;
      }

      // Generate base64 previews (no trimming on null)
      const frontImageRaw = frontCanvas
        ? await getCanvasImage(frontCanvas, true)
        : null;
      const backImageRaw = backCanvas
        ? await getCanvasImage(backCanvas, true)
        : null;

      const frontImage = frontImageRaw ? frontImageRaw.trim() : null;
      const backImage = backImageRaw ? backImageRaw.trim() : null;

      // Ensure at least one side exists
      if (!frontImage && !backImage) {
        toast({
          variant: "destructive",
          title: "Empty Design",
          description:
            "Please add something to your design before adding to cart.",
          duration: 3000,
        });
        return;
      }

      const tshirtTypeDetails = TSHIRT_TYPES[selectedType] || TSHIRT_TYPES.MEN;

      // Always provide `id` and `image`
      const designItem = {
        id: `custom-tshirt-${Date.now()}`,
        title: `Custom ${tshirtTypeDetails.name}`,
        name: `Custom ${tshirtTypeDetails.name}`,
        price: 29.99,
        image: frontImage || backImage || "", // fallback to empty string to prevent rejection
        description: `Custom designed ${tshirtTypeDetails.name.toLowerCase()} in ${tshirtColor}`,
        subtitle: "Personalized Design",
        category: {
          id: "custom-tshirts",
          name: "Custom T-Shirts",
        },
        customDesign: {
          type: selectedType,
          typeName: tshirtTypeDetails.name,
          color: tshirtColor,
          frontImage,
          backImage,
          timestamp: new Date().toISOString(),
        },
      };

      console.log("Adding to cart:", designItem);
      addDesignToCart(designItem);

      toast({
        title: "Added to Cart! 🎉",
        description: `Your custom ${tshirtTypeDetails.name.toLowerCase()} has been added to cart.`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add design to cart. Please try again.",
        duration: 3000,
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Save functionality
  const saveCanvasToFile = async (canvas, filename, includeShirt = false) => {
    try {
      const dataUrl = await getCanvasImage(canvas, includeShirt);
      if (!dataUrl) return false;

      const link = document.createElement("a");
      link.download = filename;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch (error) {
      console.error(`Error saving ${filename}:`, error);
      return false;
    }
  };

  const handleSave = async (includeShirt = false) => {
    try {
      if (!frontCanvas && !backCanvas) {
        toast({
          variant: "destructive",
          title: "No Design Found",
          description: "Please create a design before saving.",
          duration: 3000,
        });
        return;
      }

      let savedCount = 0;
      let failedCount = 0;

      if (frontCanvas) {
        const frontSaved = await saveCanvasToFile(
          frontCanvas,
          `tshirt-front-${includeShirt ? "with-shirt" : "design-only"}.png`,
          includeShirt
        );
        frontSaved ? savedCount++ : failedCount++;
      }

      if (backCanvas) {
        const backSaved = await saveCanvasToFile(
          backCanvas,
          `tshirt-back-${includeShirt ? "with-shirt" : "design-only"}.png`,
          includeShirt
        );
        backSaved ? savedCount++ : failedCount++;
      }

      if (failedCount > 0) {
        toast({
          variant: "destructive",
          title: "Save Error",
          description: `Failed to save ${failedCount} design${
            failedCount > 1 ? "s" : ""
          }.`,
          duration: 3000,
        });
      } else {
        toast({
          title: "Design Saved!",
          description: `Successfully saved ${savedCount} design file${
            savedCount > 1 ? "s" : ""
          }.`,
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        variant: "destructive",
        title: "Save Error",
        description: "An unexpected error occurred while saving.",
        duration: 3000,
      });
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full p-4 bg-white rounded-lg shadow-sm border">
      {/* T-Shirt Type Selector */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          T-Shirt Style
        </label>
        <Select value={selectedType} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select T-Shirt" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {Object.entries(TSHIRT_TYPES).map(([value, { name }]) => (
                <SelectItem key={value} value={value}>
                  {name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="h-px bg-gray-200 my-1" />

      {/* Design Tools */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Design Tools
        </label>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleAddImage}
          className="hidden"
        />

        <Button
          onClick={triggerFileInput}
          className="w-full justify-start"
          variant="outline"
        >
          <ImagePlus className="h-4 w-4 mr-2" />
          <span className="text-sm">Add Image</span>
        </Button>

        <Button
          onClick={handleAddText}
          className="w-full justify-start"
          variant="outline"
        >
          <Type className="h-4 w-4 mr-2" />
          <span className="text-sm">Add Text</span>
        </Button>

        <Button
          onClick={handleAddLine}
          className="w-full justify-start"
          variant="outline"
        >
          <Slash className="h-4 w-4 mr-2" />
          <span className="text-sm">Add Line</span>
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button className="w-full justify-start" variant="outline">
              <Palette className="h-4 w-4 mr-2" />
              <span className="text-sm">T-Shirt Color</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="ml-5 w-80">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Select Color</h4>
                <p className="text-sm text-muted-foreground">
                  Choose the base color for your t-shirt
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {TSHIRT_COLOR_CODES.map((color) => (
                  <Button
                    key={color}
                    className="w-10 h-10 rounded-full p-0 border-2 border-gray-300 shadow-sm hover:shadow-md hover:scale-110 transition-all duration-200"
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorChange(color)}
                  />
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="h-px bg-gray-200 my-1" />

      {/* Actions */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Actions
        </label>

        <Button
          onClick={handleDelete}
          variant="outline"
          className="w-full justify-start text-orange-600 hover:text-orange-700 hover:bg-orange-50"
        >
          <Trash className="h-4 w-4 mr-2" />
          <span className="text-sm">Remove</span>
        </Button>

        <Button
          onClick={handleClearAll}
          variant="outline"
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash className="h-4 w-4 mr-2" />
          <span className="text-sm">Clear All</span>
        </Button>
      </div>

      <div className="h-px bg-gray-200 my-1" />

      {/* Add to Cart - Prominent Button */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Purchase
        </label>

        <Button
          onClick={handleAddToCart}
          disabled={isAddingToCart}
          className="w-full justify-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transition-all duration-200 h-11"
        >
          <ShoppingCart className="h-5 w-5 mr-2" />
          <span className="text-sm font-semibold">
            {isAddingToCart ? "Adding..." : "Add to Cart"}
          </span>
        </Button>
      </div>

      <div className="h-px bg-gray-200 my-1" />

      {/* Save Options */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Export Design
        </label>

        <Button
          onClick={() => handleSave(true)}
          className="w-full justify-start bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-sm"
        >
          <Download className="h-4 w-4 mr-2" />
          <span className="text-sm">With T-Shirt</span>
        </Button>

        <Button
          onClick={() => handleSave(false)}
          variant="outline"
          className="w-full justify-start border-green-600 text-green-700 hover:bg-green-50"
        >
          <Save className="h-4 w-4 mr-2" />
          <span className="text-sm">Design Only</span>
        </Button>
      </div>
    </div>
  );
};

export default ToolBar;
